import * as React from 'react'
import { cn } from '../utils'
import { semanticTokens } from '@supabank/config/theme'

export type StatusChipIntent = 'quiet' | 'info' | 'success' | 'warning' | 'danger'

export interface StatusChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  intent: StatusChipIntent
  tone?: 'quiet' | 'default' | 'loud'
  size?: 'xs' | 'sm' | 'md'
  children: React.ReactNode
}

/**
 * StatusChip - Status indicator chip
 * 
 * Design Dialect: intent="success" tone="quiet" size="md"
 * 
 * @example
 * <StatusChip intent="success" tone="default" size="sm">
 *   Active
 * </StatusChip>
 */
export function StatusChip({ 
  intent, 
  tone = 'default',
  size = 'sm',
  className, 
  children,
  ...props 
}: StatusChipProps) {
  const intentClasses = {
    quiet: {
      bg: semanticTokens.bg.muted,
      fg: semanticTokens.fg.muted,
      border: semanticTokens.border.subtle,
    },
    info: {
      bg: semanticTokens.info.bg,
      fg: semanticTokens.info.fg,
      border: semanticTokens.info.border,
    },
    success: {
      bg: semanticTokens.positive.bg,
      fg: semanticTokens.positive.fg,
      border: semanticTokens.positive.border,
    },
    warning: {
      bg: semanticTokens.warning.bg,
      fg: semanticTokens.warning.fg,
      border: semanticTokens.warning.border,
    },
    danger: {
      bg: semanticTokens.danger.bg,
      fg: semanticTokens.danger.fg,
      border: semanticTokens.danger.border,
    },
  }

  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  }

  const intentStyle = intentClasses[intent]

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border font-medium',
        intentStyle.bg,
        intentStyle.fg,
        intentStyle.border,
        sizeClasses[size],
        semanticTokens.radius.md,
        semanticTokens.motion.micro,
        semanticTokens.motion.reduced,
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

